import { Button } from "@/components/ui/button";
import { ArrowDown, Leaf } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" className="relative hero-gradient pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
      {/* Floating organic ornaments */}
      <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full bg-primary/30 blur-3xl floaty" aria-hidden />
      <div className="absolute top-20 right-0 w-80 h-80 rounded-full bg-secondary/25 blur-3xl floaty" style={{animationDelay:'-3s'}} aria-hidden />
      <div className="absolute bottom-10 left-1/3 w-64 h-64 rounded-full bg-accent/10 blur-3xl floaty" style={{animationDelay:'-5s'}} aria-hidden />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-card/70 border border-border/60 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-8">
              <Leaf size={14} className="text-secondary" />
              <span>Spinal Flow · At Home</span>
            </div>

            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-foreground">
              Restore Balance,<br />
              <span className="italic text-secondary">Heal Naturally.</span>
            </h1>

            <p className="mt-7 max-w-xl text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Personalised Spinal Flow sessions in the comfort of your home —
              a gentle, deeply restorative practice for the nervous system, body, and soul.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                onClick={() => scrollTo("booking")}
                data-testid="hero-book-btn"
                className="rounded-full px-8 py-6 text-base bg-foreground text-background hover:bg-foreground/85 transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-lg"
              >
                Book a Session
              </Button>
              <Button
                onClick={() => scrollTo("what")}
                data-testid="hero-learn-btn"
                variant="outline"
                className="rounded-full px-8 py-6 text-base border-border/60 bg-card/40 hover:bg-card"
              >
                Learn More
              </Button>
            </div>

            <div className="mt-14 flex items-center gap-8 text-sm text-muted-foreground">
              <div>
                <div className="font-serif text-3xl text-foreground">12+</div>
                <div>Years of practice</div>
              </div>
              <div className="h-10 w-px bg-border" />
              <div>
                <div className="font-serif text-3xl text-foreground">500+</div>
                <div>Sessions held</div>
              </div>
              <div className="h-10 w-px bg-border hidden sm:block" />
              <div className="hidden sm:block">
                <div className="font-serif text-3xl text-foreground">4.9★</div>
                <div>Client rating</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1769184614133-e0cad8d2a1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxyZWxheGluZyUyMGhvbWUlMjBzcGElMjBpbnRlcmlvciUyMHNvZnQlMjBsaWdodHxlbnwwfHx8fDE3Nzg3Mzk5ODl8MA&ixlib=rb-4.1.0&q=85"
                alt="Soft greenery & cream fabric"
                className="rounded-[40px] w-full object-cover h-[460px] lg:h-[560px]"
              />
              <div className="absolute -bottom-6 -left-6 bg-background/90 backdrop-blur-md border border-border/60 rounded-3xl p-5 max-w-[240px] shadow-lg">
                <p className="text-xs uppercase tracking-[0.18em] text-secondary mb-1">Today's intention</p>
                <p className="font-serif text-xl leading-snug">"Soften where you've been bracing."</p>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={() => scrollTo("about")}
          className="mx-auto mt-16 flex items-center justify-center w-10 h-10 rounded-full border border-border/60 text-muted-foreground hover:text-foreground hover:bg-card transition-all"
          aria-label="Scroll down"
          data-testid="hero-scroll-indicator"
        >
          <ArrowDown size={16} />
        </button>
      </div>
    </section>
  );
}
